import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function escapeHtml(value) {
  return value === null || value === undefined ? "" : String(value);
}

function formatValue(value, fallback = "") {
  return value === null || value === undefined || value === ""
    ? fallback
    : String(value);
}

export default function RouteList() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const initialPage = Number(params.get("page") || 1);
  const initialSearch = params.get("search") || "";

  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(initialPage > 0 ? initialPage : 1);

  const [routes, setRoutes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const recordsPerPage = 10;

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [allocModalOpen, setAllocModalOpen] = useState(false);
  const [allocSaving, setAllocSaving] = useState(false);
  const [allocGuards, setAllocGuards] = useState([]);

  const [allocRoute, setAllocRoute] = useState({
    route_id: "",
    route_name: "",
  });

  const [allocForm, setAllocForm] = useState({
    guard_member_id: "",
    shift_label: "Morning",
    shift_label_custom: "",
    start_time: "",
    end_time: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token, page, search);
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    if (!token) return;

    fetchPageData(dashboardData, token, page, search);
  }, [page, search]);

  const fetchPageData = async (local, token, currentPage, currentSearch) => {
    try {
      setLoading(true);

      const [dashboardRes, routesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(
          `${API_BASE_URL}/route/list?page=${currentPage}&limit=${recordsPerPage}&search=${encodeURIComponent(
            currentSearch || ""
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!routesRes.ok) throw new Error("Failed to fetch routes");

      const dashboardResult = await dashboardRes.json();
      const routesResult = await routesRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const listData = routesResult?.data || {};

      setRoutes(Array.isArray(listData.routes) ? listData.routes : []);
      setTotalRecords(Number(listData.totalRecords || 0));
      setTotalPages(Math.max(1, Number(listData.totalPages || 1)));
    } catch (error) {
      console.error("Route list page error:", error);
      toast.error(error.message || "Failed to load route list.");
      setRoutes([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const updateUrl = (nextPage, nextSearch) => {
    const query = new URLSearchParams();

    if (nextPage > 1) query.set("page", String(nextPage));
    if (nextSearch?.trim()) query.set("search", nextSearch.trim());

    navigate(`/route_list${query.toString() ? `?${query.toString()}` : ""}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const nextSearch = searchInput.trim();

    setPage(1);
    setSearch(nextSearch);
    updateUrl(1, nextSearch);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    navigate("/route_list");
    toast.success("Search cleared");
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    updateUrl(nextPage, search);
  };

  const viewRoute = (route) => {
    setSelectedRoute(route);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedRoute(null);
    setViewModalOpen(false);
  };

  const goToRouteAllocations = (routeId, routeName) => {
    navigate(
      `/route_allocation?route_id=${routeId}&route_name=${encodeURIComponent(
        routeName || ""
      )}`
    );
  };

  const confirmDelete = async (routeId) => {
    if (!window.confirm(
      "Are you sure you want to delete this route?\n\n" +
        "This will permanently delete:\n" +
        "• Route details\n" +
        "• All checkpoints\n" +
        "• All geofences\n" +
        "• All route allocations\n" +
        "• All geofence assignments\n\n" +
        "This action cannot be undone."
    )) return;

    try {
      setDeletingId(routeId);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/route/${routeId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to delete route");
      }

      toast.success("Route and all associated data deleted successfully!");

      if (selectedRoute?.route_id === routeId) {
        closeViewModal();
      }

      await fetchPageData(dashboardData, token, page, search);
    } catch (error) {
      console.error("Delete route error:", error);
      toast.error(error.message || "Failed to delete route.");
    } finally {
      setDeletingId(null);
    }
  };

  const fetchGuards = async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/users/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        cache: "no-store",
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch guards");
      }

      setAllocGuards(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error("Fetch guards error:", error);
      toast.error("Failed to load guards list.");
      setAllocGuards([]);
    }
  };

  const openAllocationModal = async (routeId, routeName) => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);

    const pad = (n) => String(n).padStart(2, "0");

    const defaultStart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const end = new Date(now);
    end.setHours(end.getHours() + 8);

    const defaultEnd = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(
      end.getDate()
    )}T${pad(end.getHours())}:${pad(end.getMinutes())}`;

    setAllocRoute({
      route_id: routeId,
      route_name: routeName,
    });

    setAllocForm({
      guard_member_id: "",
      shift_label: "Morning",
      shift_label_custom: "",
      start_time: defaultStart,
      end_time: defaultEnd,
    });

    setAllocModalOpen(true);
    await fetchGuards();
  };

  const closeAllocModal = () => {
    setAllocModalOpen(false);

    setAllocRoute({
      route_id: "",
      route_name: "",
    });

    setAllocForm({
      guard_member_id: "",
      shift_label: "Morning",
      shift_label_custom: "",
      start_time: "",
      end_time: "",
    });

    setAllocGuards([]);
  };

  const handleAllocChange = (e) => {
    const { name, value } = e.target;

    setAllocForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveAllocation = async () => {
    if (
      !allocRoute.route_id ||
      !allocForm.guard_member_id ||
      !allocForm.shift_label ||
      !allocForm.start_time ||
      !allocForm.end_time
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (allocForm.shift_label === "Custom" && !allocForm.shift_label_custom.trim()) {
      toast.error("Please enter custom shift label.");
      return;
    }

    try {
      setAllocSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        route_id: parseInt(allocRoute.route_id, 10),
        guard_member_id: allocForm.guard_member_id,
        shift_label: allocForm.shift_label,
        shift_label_custom: allocForm.shift_label_custom,
        start_time: allocForm.start_time,
        end_time: allocForm.end_time,
      };

      const res = await fetch(`${API_BASE_URL}/route-allocation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save allocation");
      }

      toast.success(data.message || "Allocation saved successfully.");
      closeAllocModal();
    } catch (error) {
      console.error("Save allocation error:", error);
      toast.error(error.message || "Unexpected error saving allocation.");
    } finally {
      setAllocSaving(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/create_route" : "/route-master1"),
    [dashboardData.user_map]
  );

  const paginationItems = useMemo(() => {
    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    if (start > 1) {
      items.push(1);
      if (start > 2) items.push("...");
    }

    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) items.push("...");
      items.push(totalPages);
    }

    return items;
  }, [page, totalPages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading route list...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="routes"
      activeMenu="routes"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />
      
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Route Management
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Manage route records, checkpoints, and guard allocations
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Route List
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                View all routes and quickly manage checkpoints, route details, and allocations
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate("/escort_list")}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Escort Lists
              </button>
              <button
                type="button"
                onClick={() => navigate("/create_route")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Add New Route
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Routes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {totalRecords}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Current Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {page} / {totalPages}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Per Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {recordsPerPage}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Status
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {routes.length > 0 ? "Active" : "Empty"}
            </p>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <form onSubmit={handleSearchSubmit} className="flex gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Search by route name, from, or to..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-[260px] rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <button
              type="submit"
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Search
            </button>

            {search ? (
              <button
                type="button"
                onClick={clearSearch}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
              >
                Clear
              </button>
            ) : null}
          </form>
        </div>

        {/* Routes Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-5 py-[18px] border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0">
              Routes Database
            </p>
            <span className="text-[12px] text-[var(--text-muted)]">
              {totalRecords} entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse">
              <thead>
                <tr>
                  {["S. No", "Route Name", "From", "To", "Checkpoints", "Allocations", "Actions"].map((head) => (
                    <th
                      key={head}
                      className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {routes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-[var(--text-muted)]">
                      {search ? `No routes match your search: "${search}"` : "No routes found in the database."}
                    </td>
                  </tr>
                ) : (
                  routes.map((row, index) => {
                    const routeId = Number(row.route_id);
                    const routeName = row.r_name || "";

                    const checkpoints = row.checkpoints
                      ? String(row.checkpoints)
                          .split("|||")
                          .filter((s) => s !== "")
                      : [];

                    return (
                      <tr key={routeId} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                        <td className="px-[18px] py-[15px] text-[13px] align-middle">
                          <span className="text-[var(--accent)] font-extrabold">
                            {(page - 1) * recordsPerPage + index + 1}
                          </span>
                        </td>

                        <td className="px-[18px] py-[15px] text-[13px] align-middle">
                          <div className="font-extrabold text-[var(--text-primary)]">
                            {formatValue(row.r_name)}
                          </div>
                          <div className="text-[11px] text-[var(--text-muted)]">
                            ID: {routeId}
                          </div>
                        </td>

                        <td className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] align-middle">
                          {formatValue(row.r_from)}
                        </td>

                        <td className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] align-middle">
                          {formatValue(row.r_to)}
                        </td>

                        <td className="px-[18px] py-[15px] text-[13px] align-middle">
                          {checkpoints.length > 0 ? (
                            <>
                              <div className="flex gap-2 flex-wrap">
                                {checkpoints.slice(0, 3).map((checkpoint, i) => (
                                  <span
                                    key={`${routeId}-${i}`}
                                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)] font-extrabold text-[11px]"
                                  >
                                    {escapeHtml(checkpoint)}
                                  </span>
                                ))}

                                {checkpoints.length > 3 ? (
                                  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-muted)] font-extrabold text-[11px]">
                                    +{checkpoints.length - 3} more
                                  </span>
                                ) : null}
                              </div>

                              <div className="mt-2 text-[11px] text-[var(--text-muted)]">
                                Total: {Number(row.checkpoint_count || 0)} checkpoints
                              </div>
                            </>
                          ) : (
                            <span className="text-[var(--text-muted)] italic text-[12px]">
                              No checkpoints
                            </span>
                          )}
                        </td>

                        <td className="px-[18px] py-[15px] text-[13px] align-middle">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => openAllocationModal(routeId, routeName)}
                              className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                              style={{
                                background: "#f59e0b",
                                color: "white",
                              }}
                            >
                              Allocate
                            </button>

                            <button
                              type="button"
                              onClick={() => goToRouteAllocations(routeId, routeName)}
                              className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                              style={{
                                background: "#0ea5e9",
                                color: "white",
                              }}
                            >
                              View
                            </button>
                          </div>
                        </td>

                        <td className="px-[18px] py-[15px] text-[13px] align-middle">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => navigate(`/create_route?edit_id=${routeId}`)}
                              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] hover:text-[var(--accent)]"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              onClick={() => viewRoute(row)}
                              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] hover:text-[var(--accent)]"
                            >
                              View
                            </button>

                            <button
                              type="button"
                              onClick={() => confirmDelete(routeId)}
                              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--red-faint)] hover:border-[var(--red-border)] hover:text-[var(--red)] disabled:opacity-60 disabled:cursor-not-allowed"
                              disabled={deletingId === routeId}
                            >
                              {deletingId === routeId ? "..." : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="px-5 py-[15px] border-t border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] text-[var(--text-muted)]">
                Showing {(page - 1) * recordsPerPage + 1} to{" "}
                {Math.min(page * recordsPerPage, totalRecords)} of {totalRecords} entries
              </div>

              <div className="flex gap-1.5 flex-wrap">
                {page > 1 ? (
                  <button
                    type="button"
                    onClick={() => handlePageChange(page - 1)}
                    className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
                  >
                    Previous
                  </button>
                ) : null}

                {paginationItems.map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-[var(--text-muted)] text-[16px] font-extrabold px-1 self-center">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handlePageChange(item)}
                      className={`border rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer ${
                        item === page
                          ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
                          : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)]"
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}

                {page < totalPages ? (
                  <button
                    type="button"
                    onClick={() => handlePageChange(page + 1)}
                    className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
                  >
                    Next
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* View Modal */}
      {viewModalOpen && selectedRoute ? (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 z-[2500]" onClick={closeViewModal}>
          <div className="w-full max-w-[900px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--divider)]">
              <h3 className="m-0 text-[22px] font-extrabold text-[var(--text-primary)]">
                Route Details
              </h3>
              <button
                type="button"
                onClick={closeViewModal}
                className="border-0 bg-transparent text-3xl leading-none cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                ×
              </button>
            </div>

            <div className="p-6 bg-[var(--surface)]">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--surface-border)] p-4">
                  <p className="mb-1.5 text-[11px] text-[var(--text-muted)] font-extrabold uppercase tracking-wider">
                    Route ID
                  </p>
                  <p className="text-[15px] text-[var(--text-primary)] font-extrabold">
                    {selectedRoute.route_id}
                  </p>
                </div>

                <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--surface-border)] p-4">
                  <p className="mb-1.5 text-[11px] text-[var(--text-muted)] font-extrabold uppercase tracking-wider">
                    Route Name
                  </p>
                  <p className="text-[15px] text-[var(--text-primary)] font-extrabold">
                    {formatValue(selectedRoute.r_name)}
                  </p>
                </div>

                <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--surface-border)] p-4">
                  <p className="mb-1.5 text-[11px] text-[var(--text-muted)] font-extrabold uppercase tracking-wider">
                    From Location
                  </p>
                  <p className="text-[15px] text-[var(--text-primary)]">
                    {formatValue(selectedRoute.r_from, "Not specified")}
                  </p>
                </div>

                <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--surface-border)] p-4">
                  <p className="mb-1.5 text-[11px] text-[var(--text-muted)] font-extrabold uppercase tracking-wider">
                    To Location
                  </p>
                  <p className="text-[15px] text-[var(--text-primary)]">
                    {formatValue(selectedRoute.r_to, "Not specified")}
                  </p>
                </div>

                <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--surface-border)] p-4">
                  <p className="mb-1.5 text-[11px] text-[var(--text-muted)] font-extrabold uppercase tracking-wider">
                    Total Checkpoints
                  </p>
                  <p className="text-[15px] text-[var(--text-primary)] font-extrabold">
                    {Number(selectedRoute.checkpoint_count || 0)} checkpoints
                  </p>
                </div>

                <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--surface-border)] p-4">
                  <p className="mb-1.5 text-[11px] text-[var(--text-muted)] font-extrabold uppercase tracking-wider">
                    Status
                  </p>
                  <span className="inline-flex px-3 py-1.5 rounded-full text-[11.5px] font-extrabold bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]">
                    ● Active
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="m-0 mb-3 text-[16px] font-extrabold text-[var(--text-primary)]">
                  Checkpoints
                </h4>

                {selectedRoute.checkpoints ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {String(selectedRoute.checkpoints)
                      .split("|||")
                      .filter(Boolean)
                      .map((checkpoint, index) => (
                        <div key={index} className="bg-[var(--card-bg)] rounded-2xl border border-[var(--surface-border)] p-4">
                          <span className="inline-flex px-3 py-1.5 rounded-full bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)] font-extrabold text-[11px]">
                            Checkpoint {index + 1}
                          </span>
                          <p className="mt-3 text-[13px] text-[var(--text-primary)] font-semibold">
                            {checkpoint}
                          </p>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-[var(--surface-border)] rounded-2xl bg-[var(--card-bg)] p-6 text-center text-[var(--text-muted)] text-[13px]">
                    No checkpoints defined for this route
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-[var(--card-bg)] border-t border-[var(--divider)] flex justify-end gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(`/create_route?edit_id=${selectedRoute.route_id}`)}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Edit Route
              </button>

              <button
                type="button"
                onClick={() => confirmDelete(selectedRoute.route_id)}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90 bg-[var(--red)] text-white"
              >
                Delete Route
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Allocation Modal */}
      {allocModalOpen ? (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 z-[2500]" onClick={closeAllocModal}>
          <div className="w-full max-w-[800px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--divider)]">
              <h3 className="m-0 text-[22px] font-extrabold text-[var(--text-primary)]">
                Allocate Guard to Route
              </h3>
              <button
                type="button"
                onClick={closeAllocModal}
                className="border-0 bg-transparent text-3xl leading-none cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                ×
              </button>
            </div>

            <div className="p-6 bg-[var(--surface)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Route
                  </label>
                  <input
                    type="text"
                    value={allocRoute.route_name}
                    disabled
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Shift
                  </label>
                  <select
                    name="shift_label"
                    value={allocForm.shift_label}
                    onChange={handleAllocChange}
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  >
                    <option value="Morning">Morning</option>
                    <option value="Evening">Evening</option>
                    <option value="Night">Night</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Guard <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <select
                    name="guard_member_id"
                    value={allocForm.guard_member_id}
                    onChange={handleAllocChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  >
                    <option value="">
                      {allocGuards.length ? "-- Select Guard --" : "Loading guards..."}
                    </option>
                    {allocGuards.map((guard) => (
                      <option key={guard.member_id} value={guard.member_id}>
                        {guard.username} ({guard.member_id})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Start Date & Time <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={allocForm.start_time}
                    onChange={handleAllocChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    End Date & Time <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={allocForm.end_time}
                    onChange={handleAllocChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>

                {allocForm.shift_label === "Custom" ? (
                  <div className="md:col-span-2">
                    <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                      Custom Shift Label
                    </label>
                    <input
                      type="text"
                      name="shift_label_custom"
                      value={allocForm.shift_label_custom}
                      onChange={handleAllocChange}
                      placeholder="e.g. OT Shift / Special Duty"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="px-6 py-4 bg-[var(--card-bg)] border-t border-[var(--divider)] flex justify-end gap-3 flex-wrap">
              <button
                type="button"
                onClick={closeAllocModal}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Close
              </button>

              <button
                type="button"
                onClick={saveAllocation}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
                disabled={allocSaving}
              >
                {allocSaving ? "Saving..." : "Save Allocation"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Layout>
  );
}